import {omit} from 'lodash';
import {useMemo} from 'react';

import {useOptionalPart, usePart} from '../hooks';
import {usePanelBankConfig} from '../panelBankConfig/hooks';
import {usePanelBankSectionConfig} from '../panelBankSectionConfig/hooks';
import {useOptionalPanels} from '../panels/hooks';
import {useRunSets} from '../runSet/hooks';
import {type WholeFromTypeWithRef} from '../types';
import type * as SectionTypes from './types';

export function useSection(
  ref: SectionTypes.Ref
): WholeFromTypeWithRef<'section'> {
  const section = usePart(ref);
  const runSets = useRunSets(section.runSetRefs);
  const panels = useOptionalPanels(section.panelsRef);
  const panelBankConfig = usePanelBankConfig(section.panelBankConfigRef);

  const panelBankSectionConfig = usePanelBankSectionConfig(
    section.panelBankSectionConfigRef
  );
  const settings = useOptionalPart(section.panelSettingsRef);
  const customRunColors = usePart(section.customRunColorsRef);
  const customRunNames = usePart(section.customRunNamesRef);
  const workspaceSettings = usePart(section.workspaceSettingsRef);
  const semanticLegendSettings = usePart(section.semanticLegendRef);
  return useMemo(
    () => ({
      // Do not include ref pointers in the resulting object—these values end up
      // getting saved to the server in reports, and the ref pointers confuse
      // the python-based Reports API. These properties do not conform to the
      // `WholeFromTypeWithRef<'section'>` type, either, so it should be safe
      // to omit them and expect no breakage.
      ...omit(section, [
        'runSetRefs',
        'panelsRef',
        'panelBankConfigRef',
        'panelBankSectionConfigRef',
        'panelSettingsRef',
        'customRunColorsRef',
        'customRunNamesRef',
        'semanticLegendRef',
        'workspaceSettingsRef',
      ]),
      runSets: runSets,
      panels: panels,
      panelBankConfig: panelBankConfig,
      panelBankSectionConfig: panelBankSectionConfig,
      settings: settings,
      customRunColors: customRunColors,
      customRunNames: customRunNames,
      semanticLegendSettings: semanticLegendSettings,
      workspaceSettings: workspaceSettings,
      ref,
    }),
    [
      ref,
      section,
      runSets,
      panels,
      panelBankConfig,
      panelBankSectionConfig,
      settings,
      customRunColors,
      customRunNames,
      semanticLegendSettings,
      workspaceSettings,
    ]
  );
}
